import { useCallback, useEffect, useState } from "react";
import { BadgeCheck, Handshake, HeartHandshake, LoaderCircle, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS_META = {
  pending_validation: { label: "À valider", cls: "bg-amber-100 text-amber-700" },
  pending_payment: { label: "Paiement en attente", cls: "bg-slate-100 text-slate-500" },
  active: { label: "Actif", cls: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejeté", cls: "bg-rose-100 text-rose-600" },
};

function PartnerCard({ p, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState("");
  const st = STATUS_META[p.partner.status] || STATUS_META.pending_payment;

  const act = async (path, body, okMsg) => {
    setBusy(true);
    try {
      await api.post(`/admin/partners/${p.id}/${path}`, body || {});
      toast.success(okMsg);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action impossible.");
    } finally {
      setBusy(false);
      setRejectOpen(false);
    }
  };

  return (
    <div className="card-integra p-4" data-testid="admin-partner-card">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-bold text-slate-900">{p.partner.organization}</p>
        {p.partner.is_nonprofit && (
          <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-bold text-emeraude">
            <HeartHandshake className="h-2.5 w-2.5" /> Bénévolat
          </span>
        )}
        <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${st.cls}`} data-testid="admin-partner-status-chip">{st.label}</span>
        <span className="ml-auto text-[10px] text-slate-400">
          {p.partner.monthly_amount > 0 ? `${(p.partner.monthly_amount / 100).toLocaleString("fr-FR", { style: "currency", currency: "EUR" })}/mois` : "Gratuit"}
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-500">
        {p.name} · {p.email} · {p.partner.category}
        {p.created_at && ` · inscrit le ${new Date(p.created_at).toLocaleDateString("fr-FR")}`}
      </p>

      {(p.partner.status === "pending_validation" || p.partner.status === "rejected") && !rejectOpen && (
        <div className="mt-3 flex gap-2">
          <button disabled={busy} onClick={() => act("validate", null, "Partenaire validé : sa fiche est désormais visible dans l'annuaire.")}
            className="flex items-center gap-1.5 rounded-full bg-emeraude px-4 py-2 text-[11px] font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
            data-testid="admin-partner-validate-button">
            {busy ? <LoaderCircle className="h-3 w-3 animate-spin" /> : <BadgeCheck className="h-3 w-3" />} Valider
          </button>
          {p.partner.status !== "rejected" && (
            <button disabled={busy} onClick={() => setRejectOpen(true)}
              className="flex items-center gap-1.5 rounded-full border border-rouge/40 px-4 py-2 text-[11px] font-bold text-rouge transition-colors hover:bg-rouge hover:text-white disabled:opacity-50"
              data-testid="admin-partner-reject-button">
              <X className="h-3 w-3" /> Rejeter
            </button>
          )}
        </div>
      )}
      {rejectOpen && (
        <div className="mt-3 space-y-2">
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} autoFocus
            placeholder="Motif du rejet (envoyé par email au partenaire, facultatif)…"
            className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-xs focus:border-rouge focus:outline-none" data-testid="admin-partner-reject-reason" />
          <div className="flex gap-2">
            <button disabled={busy} onClick={() => act("reject", { reason: reason.trim() || null }, "Partenaire rejeté. Un email lui a été envoyé.")}
              className="rounded-full bg-rouge px-4 py-2 text-[11px] font-bold text-white hover:bg-red-700 disabled:opacity-50" data-testid="admin-partner-reject-confirm">
              {busy ? <LoaderCircle className="h-3 w-3 animate-spin" /> : "Confirmer le rejet"}
            </button>
            <button onClick={() => setRejectOpen(false)} className="rounded-full border border-slate-200 px-4 text-[11px] font-semibold text-slate-500">Annuler</button>
          </div>
        </div>
      )}
      {p.partner.status === "active" && (
        <button disabled={busy} onClick={() => setRejectOpen(true)}
          className="mt-3 rounded-full border border-slate-200 px-4 py-2 text-[11px] font-bold text-slate-500 transition-colors hover:border-rouge/40 hover:text-rouge"
          data-testid="admin-partner-deactivate-button">
          Retirer de l'annuaire
        </button>
      )}
    </div>
  );
}

export default function AdminPartners() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => {
    api.get("/admin/partners", { params: filter ? { status: filter } : {} })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Impossible de charger les partenaires."));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-slate-300" /></div>;

  const chips = [
    { value: "", label: `Tous (${data.total})` },
    ...Object.entries(STATUS_META).map(([value, m]) => ({ value, label: `${m.label} (${data.counts[value] ?? 0})` })),
  ];

  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="page-admin-partners">
      <div>
        <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Partenaires</h1>
        <p className="mt-1 text-sm text-slate-500">
          Validez les comptes bénévoles avant leur apparition dans l'annuaire. Les comptes payants s'activent automatiquement après paiement Stripe.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="admin-partners-filters">
        {chips.map((c) => (
          <button key={c.value} onClick={() => setFilter(c.value)}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${
              filter === c.value ? "bg-bleu text-white" : "border border-slate-200 bg-white text-slate-500 hover:border-bleu/40 hover:text-bleu"
            }`}
            data-testid={`admin-partners-filter-${c.value || "all"}`}>
            {c.label}
          </button>
        ))}
      </div>

      {data.partners.length === 0 ? (
        <div className="card-integra p-10 text-center" data-testid="admin-partners-empty">
          <Handshake className="mx-auto h-10 w-10 text-slate-200" />
          <p className="mt-3 text-sm font-bold text-slate-700">Aucun partenaire {filter ? "avec ce statut" : ""}</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {data.partners.map((p) => <PartnerCard key={p.id} p={p} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}
